import moment from 'moment';

/**
 * Formatadores da aplicação, expostos como métodos globais prefixados com `$`.
 *
 * Substituem os filtros de template (`{{ x | currency }}`), que foram removidos do
 * código — eram 512 em 97 arquivos — e não existem no Vue 3. As implementações vieram
 * de `src/filters/filters.js` sem alteração: mudou a forma de chamar, nunca o que
 * aparece na tela. Aquele arquivo foi apagado quando o placar zerou os filtros de
 * template e nenhum `Vue.filter` sobrou em uso.
 *
 * O PREFIXO `$` É OBRIGATÓRIO e não é estilo. Sete componentes já declaram um método
 * local chamado `formatDate` ou `formatDateTime`, e pelo menos um deles diverge do
 * filtro global: `src/components/Cadastro_view/recorrencias-component.vue` devolve
 * `DD/MM/YYYY` em horário local, enquanto o filtro devolve `DD/MM/YY` em UTC. Esse mesmo
 * arquivo usa `| formatDate` quatro vezes. Registrar os globais sem prefixo faria essas
 * quatro linhas passarem a chamar o método local e mudarem de saída — data diferente na
 * tela, sem erro nenhum.
 */

/** Data pura vinda do backend. UTC de propósito: evita shift de dia. */
export const formatDate = value => {
  if (!value) return '';
  return moment.utc(value).format('DD/MM/YY');
};

export const formatDateTime = value => {
  if (!value) return '';
  return moment(value).format('DD/MM/YYYY HH:mm') + 'h';
};

export const formatTime = value => {
  if (!value) return '';
  return moment(value).format('HH:mm');
};

export const fromNow = value => {
  if (!value) return '';
  return moment(value).fromNow();
};

export const currency = value => {
  if (!value) return '0,00';
  var val = (value / 1).toFixed(2).replace('.', ',');
  return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

/**
 * Rótulo de evento de Tele Sena.
 *
 * Existem eventos homônimos no cadastro (o nome vem do fornecedor e às vezes repete, com
 * diferença só de espaço/TAB), e escolher o errado quebra o casamento com o estoque. Por isso
 * o rótulo carrega valor e resgate, que é o que distingue um do outro na tela.
 */
export const eventoLabel = evento => {
  if (!evento) return 'Item Vazio';
  // Seleção vazia dos modais é `{}`: sem isso o campo exibia "Evento undefined"
  if (evento.id == null && !String(evento.nome || '').trim()) return '';

  let label = String(evento.nome || '').trim() || `Evento ${evento.id}`;

  if (Number(evento.valor) > 0) label += ` — R$ ${currency(evento.valor)}`;
  if (evento.resgate) label += ' (Resgate)';

  return label;
};

/**
 * A conversão que o modificador `.number` do `v-model` fazia.
 *
 * No Vue 2 é o COMPILADOR que aplica: `v-model.number="x.y"` vira
 * `$set(x, "y", _n($$v))`, onde `_n` é o `toNumber` do runtime. No Vue 3 o
 * modificador vira a prop `modelModifiers` e cabe ao componente implementá-la — e o
 * QInput 2.33.2 implementa só `trim` e `lazy`. O model passaria a guardar String onde
 * hoje guarda Number, sem erro e sem aviso, em campos de quantidade de estoque, dias
 * de alerta, percentual de comissão e valor de meta.
 *
 * A semântica é a do Vue 2, copiada do fonte e não reinventada:
 *
 *     function toNumber(val) { const n = parseFloat(val); return isNaN(n) ? val : n; }
 *
 * Devolver o VALOR ORIGINAL quando não é número — e não `NaN` — é o detalhe que
 * importa: `Number("")` é 0 e `Number("abc")` é NaN, e os dois estragariam campo
 * financeiro de formas diferentes.
 */
export const numero = valor => {
  const n = parseFloat(valor);
  return isNaN(n) ? valor : n;
};

export const decode = value => JSON.parse(value);

export const hora = value => moment(value).format('HH:mm');

export const cnpj = value =>
  String(value).replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');

export const value = (val, type) => {
  if (type === '%') {
    return `${val} %`;
  }
  return `R$ ${val}`;
};

export const truncate = (val, size = 40) => {
  val = val ? String(val) : '';

  if (val.length > size) {
    return val.slice(0, size - 3) + '...';
  }

  return val;
};

export const formatters = {
  formatDate,
  formatDateTime,
  formatTime,
  fromNow,
  currency,
  evento: eventoLabel,
  numero,
  decode,
  hora,
  cnpj,
  value,
  truncate,
};

/**
 * Installs the formatters as global properties of the app.
 *
 * Vue 2 did `Vue.prototype.$x = fn` when this file was imported; Vue 3 needs the app
 * instance, so main.js calls `instalarFormatters(app)`. Templates are unchanged.
 */
export function instalarFormatters(app) {
  for (const [nome, fn] of Object.entries(formatters)) {
    app.config.globalProperties[`$${nome}`] = fn;
  }
}

export default formatters;
